/**
 * Mundalo - Portal City Guide Detail (magazine edition).
 *
 * Fetches GET /api/city-guides/{slug}/full. Renders the gated full guide as a
 * long-form editorial layout:
 *   • Full-bleed hero with overlaid title + state eyebrow + lang toggle
 *   • Sticky chapter navigation
 *   • Numbered section headers with ornamental dividers
 *   • Cloudinary art-directed section images (variants of the hero)
 *   • Pull quotes between sections, sourced from the existing intro/narrative
 *   • Magazine-style rating panel + "Who should move here" profile cards
 *
 * Access gating:
 *   • 402 from API → upgrade-style paywall view
 *   • 200 → render the magazine
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Lock, Star, Coins, Shield, Heart, Home, Sparkles,
  Users, Briefcase, GraduationCap, Truck, Wifi, Utensils, MapPin,
} from 'lucide-react';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import MagazineLayout from './CityGuideMagazine';
import { extractApiError } from '../../lib/apiError';
import { notifyError } from '../../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Cloudinary transform helper: swaps the transformation segment of a known
// Cloudinary upload URL. Leaves non-Cloudinary URLs untouched.
const cldTransform = (url, transform) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/image/upload/')) return url;
  return url.replace(/\/image\/upload\/[^/]*\//, `/image/upload/${transform}/`);
};

// Each section id maps to a (icon, art-direction) tuple. The image variant
// is a different Cloudinary crop / effect of the same hero asset, giving
// every section its own visual signature without needing additional uploads.
const SECTION_META = {
  budget:          { icon: Coins,         crop: 'w_2000,h_900,c_fill,g_auto:subject,q_auto,f_auto' },
  safety:          { icon: Shield,        crop: 'w_2000,h_900,c_fill,g_north,e_sharpen:80,q_auto,f_auto' },
  healthcare:      { icon: Heart,         crop: 'w_2000,h_900,c_fill,g_south,e_saturation:-30,q_auto,f_auto' },
  housing:         { icon: Home,          crop: 'w_2000,h_900,c_fill,g_west,q_auto,f_auto' },
  quality_of_life: { icon: Sparkles,      crop: 'w_2000,h_900,c_fill,g_east,e_brightness:6,q_auto,f_auto' },
  social_life:     { icon: Users,         crop: 'w_2000,h_900,c_fill,g_center,e_vibrance:30,q_auto,f_auto' },
  education:       { icon: GraduationCap, crop: 'w_2000,h_900,c_fill,g_auto:faces,q_auto,f_auto' },
  employment:      { icon: Briefcase,     crop: 'w_2000,h_900,c_fill,g_north_west,e_contrast:15,q_auto,f_auto' },
  moving:          { icon: Truck,         crop: 'w_2000,h_900,c_fill,g_south_east,q_auto,f_auto' },
  internet:        { icon: Wifi,          crop: 'w_2000,h_900,c_fill,g_south_west,e_blur:80,q_auto,f_auto' },
  food:            { icon: Utensils,      crop: 'w_2000,h_900,c_fill,g_north_east,e_saturation:30,q_auto,f_auto' },
};
const fallbackMeta = { icon: MapPin, crop: 'w_2000,h_900,c_fill,g_auto,q_auto,f_auto' };

// Pull the first sentence-shaped pull quote out of a section, prioritising
// `intro` (usually the editorial summary) and falling back to `narrative`.
const extractPullQuote = (section) => {
  const source = section?.intro || section?.narrative || '';
  if (!source) return null;
  const cleaned = String(source).replace(/\s+/g, ' ').trim();
  // Find the first sentence end (., !, ? or em-dash) between 60-220 chars.
  const m = cleaned.match(/^(.{60,220}?[.!?])\s/);
  const quote = (m ? m[1] : cleaned.slice(0, 200)).trim();
  return quote.length < 40 ? null : quote;
};

// Reading-time estimate for the issue masthead.
const estimateReadTime = (sections) => {
  const words = sections.reduce((acc, s) => {
    const txt = `${s.intro || ''} ${s.narrative || ''}`;
    return acc + txt.split(/\s+/).filter(Boolean).length;
  }, 0);
  return Math.max(4, Math.round(words / 220));
};

const PortalCityGuideDetail = () => {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('en');
  const [showUpsell, setShowUpsell] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef({});

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/city-guides/${citySlug}/full?lang=${lang}`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 402) {
          const body = await r.json().catch(() => ({}));
          setError({ kind: 'paywall', body });
          return null;
        }
        if (!r.ok) {
          setError({ kind: 'error', status: r.status });
          return null;
        }
        return r.json();
      })
      .then((d) => {
        if (d) {
          setData(d);
          if (d.show_third_view_upsell) setShowUpsell(true);
        }
        setLoading(false);
      })
      .catch(() => { setError({ kind: 'network' }); setLoading(false); });
  }, [citySlug, lang]);

  // Active-section tracker for the sticky chapter nav.
  const sections = data?.full_guide?.sections || [];
  useEffect(() => {
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.dataset.sectionId);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sections.length]);

  const dismissUpsell = async () => {
    setShowUpsell(false);
    try {
      await fetch(`${API}/api/city-guides/dismiss-third-view-upsell`, {
        method: 'POST', credentials: 'include',
      });
    } catch { /* fire-and-forget */ }
  };

  const startUpsellCheckout = () => {
    setShowUpsell(false);
    navigate('/portal/city-guides?upsell=1');
  };

  const startCheckout = async () => {
    try {
      const r = await fetch(`${API}/api/city-guides/${citySlug}/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          success_path: `/portal/city-guides/${citySlug}`,
          cancel_path: `/portal/city-guides`,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.url) {
        window.location.href = d.url;
      } else if (d.already_owned) {
        window.location.reload();
      } else if (r.status === 402 && d?.detail?.error === 'tier_required') {
        navigate('/portal/upgrade?reason=cityGuides');
      } else {
        notifyError(extractApiError(d, 'Could not start checkout - please try again.'));
      }
    } catch {
      notifyError('Network error - please try again.');
    }
  };

  if (loading) {
    return (
      <PortalLayout activeTab="cityGuides">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 text-[#5A6B73]">Loading guide…</div>
      </PortalLayout>
    );
  }

  if (error?.kind === 'paywall') {
    return (
      <PortalLayout activeTab="cityGuides">
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="city-guide-paywall">
          <Lock className="w-12 h-12 text-[#E8A830] mx-auto mb-6" />
          <h2 className="font-display text-3xl md:text-5xl text-[#0A1F2E] mb-4">Unlock this guide</h2>
          <p className="text-base text-[#0A1F2E]/75 mb-8">
            Get full access to the {citySlug.replace(/-/g, ' ')} guide - neighborhoods, budgets, healthcare, schools, and the move-in plan.
          </p>
          <button
            onClick={startCheckout}
            data-testid="city-guide-paywall-cta"
            className="bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-medium px-7 py-3.5 rounded-full"
          >
            Get Full Guide - $4.99
          </button>
        </div>
      </PortalLayout>
    );
  }

  if (error?.kind === 'error' || error?.kind === 'network' || !data) {
    return (
      <PortalLayout activeTab="cityGuides">
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-16 text-center text-[#5A6B73]">
          We couldn't load this guide. <Link to="/portal/city-guides" className="text-[#2CA8A0] underline">Go back</Link>.
        </div>
      </PortalLayout>
    );
  }

  const { name, state, hero_image_url } = data;
  const scorecard = data.full_guide?.scorecard || {};
  const profiles = data.full_guide?.who_should_move || [];
  const hasMagazineData = !!data.magazine_data;

  return (
    <PortalLayout activeTab="cityGuides">
      <SEO title={`${name} City Guide - Mundalo`} description={`Full Mundalo city guide for ${name}, ${state}.`} path={`/portal/city-guides/${citySlug}`} />

      <article className="bg-[#F9F6EF] text-[#0A1F2E]" data-testid="city-guide-detail">

        {/* ============ HERO ============ */}
        <Hero
          name={name}
          state={state}
          heroUrl={hero_image_url}
          hasEs={data.has_es}
          lang={lang}
          setLang={setLang}
          readTime={estimateReadTime(sections)}
          sectionCount={sections.length || 12}
        />

        {/* ============ MASTHEAD + BACK LINK ============ */}
        <div className="max-w-5xl mx-auto px-4 md:px-8 pt-8 md:pt-12">
          <Link to="/portal/city-guides" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-[#1A5C6E] hover:text-[#0A1F2E] mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> All city guides
          </Link>
        </div>

        {hasMagazineData ? (
          // ============ NEW: Iter-58 deep magazine layout ============
          <MagazineLayout data={data} />
        ) : (
          <>
            {/* ============ LEGACY full_guide.sections layout ============ */}
            <ChapterNav sections={sections} activeSection={activeSection} />

            <div className="max-w-3xl mx-auto px-4 md:px-8 pb-12">
              {sections.map((s, idx) => (
                <Section
                  key={s.id}
                  section={s}
                  index={idx + 1}
                  heroUrl={hero_image_url}
                  registerRef={(el) => { if (el) sectionRefs.current[s.id] = el; }}
                />
              ))}
            </div>

            {Object.keys(scorecard).length > 0 && (
              <RatingPanel scorecard={scorecard} />
            )}
            {profiles.length > 0 && (
              <ProfileSpread profiles={profiles} />
            )}
          </>
        )}

        {/* ============ COLOPHON ============ */}
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center">
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="block h-px w-12 bg-[#0A1F2E]/20" />
            <Sparkles className="w-4 h-4 text-[#E8A830]" />
            <span className="block h-px w-12 bg-[#0A1F2E]/20" />
          </div>
          <p className="font-mono uppercase text-[10px] tracking-[0.25em] text-[#0A1F2E]/55">
            End of issue · Mundalo City Series
          </p>
          <p className="mt-2 text-sm text-[#0A1F2E]/55 max-w-md mx-auto">
            Updated continuously by Mundalo researchers + the Connected community.
          </p>
        </div>
      </article>

      {showUpsell && (
        <ThirdViewUpsellModal
          cityName={data.name}
          onDismiss={dismissUpsell}
          onCheckout={startUpsellCheckout}
        />
      )}
    </PortalLayout>
  );
};


// ============== SUB-COMPONENTS ==============

const Hero = ({ name, state, heroUrl, hasEs, lang, setLang, readTime, sectionCount }) => {
  const bg = useMemo(
    () => (heroUrl ? cldTransform(heroUrl, 'w_2400,h_1400,c_fill,g_auto,q_auto,f_auto') : null),
    [heroUrl]
  );
  return (
    <header
      className="relative w-full overflow-hidden bg-[#0A1F2E] text-white"
      data-testid="city-guide-hero"
      style={{ minHeight: 'min(80vh, 680px)' }}
    >
      {bg && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bg})` }}
          aria-hidden="true"
        />
      )}
      {/* Layered gradients for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F2E] via-[#0A1F2E]/55 to-[#0A1F2E]/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F2E]/60 via-transparent to-transparent" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16 lg:py-24 min-h-[inherit] flex flex-col justify-end" style={{ minHeight: 'min(80vh, 680px)' }}>
        <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#E8A830] mb-3">
          Mundalo · {state}
        </p>
        <h1 className="font-display italic text-5xl md:text-7xl lg:text-[88px] text-white leading-[0.95] mb-4 max-w-3xl">
          {name}
        </h1>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-white/80 text-sm">
          <span className="font-mono uppercase tracking-[0.15em] text-[11px]">
            {sectionCount} chapters
          </span>
          <span className="opacity-50">·</span>
          <span className="font-mono uppercase tracking-[0.15em] text-[11px]">
            {readTime} min read
          </span>
          {hasEs && (
            <>
              <span className="opacity-50">·</span>
              <span className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full p-1 border border-white/15" data-testid="cg-lang-toggle">
                <button
                  type="button"
                  onClick={() => setLang('en')}
                  className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest rounded-full transition ${lang === 'en' ? 'bg-white text-[#0A1F2E]' : 'text-white/80'}`}
                  data-testid="cg-lang-en"
                >EN</button>
                <button
                  type="button"
                  onClick={() => setLang('es')}
                  className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest rounded-full transition ${lang === 'es' ? 'bg-white text-[#0A1F2E]' : 'text-white/80'}`}
                  data-testid="cg-lang-es"
                >ES</button>
              </span>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const ChapterNav = ({ sections, activeSection }) => (
  <nav
    className="sticky top-16 z-20 bg-[#F9F6EF]/90 backdrop-blur border-y border-[#EFE6CF] mb-10"
    data-testid="city-guide-chapter-nav"
  >
    <div className="max-w-5xl mx-auto px-4 md:px-8 overflow-x-auto">
      <ul className="flex items-center gap-x-6 py-3 whitespace-nowrap">
        {sections.map((s, idx) => {
          const meta = SECTION_META[s.id] || fallbackMeta;
          const Icon = meta.icon;
          const isActive = activeSection === s.id;
          return (
            <li key={s.id}>
              <a
                href={`#section-${s.id}`}
                className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] transition ${
                  isActive ? 'text-[#0A1F2E]' : 'text-[#0A1F2E]/45 hover:text-[#0A1F2E]/75'
                }`}
                data-testid={`chapter-link-${s.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="text-[#E8A830]">{String(idx + 1).padStart(2, '0')}</span>
                <span>{s.title}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  </nav>
);

const Section = ({ section, index, heroUrl, registerRef }) => {
  const meta = SECTION_META[section.id] || fallbackMeta;
  const Icon = meta.icon;
  const sectionImage = heroUrl ? cldTransform(heroUrl, meta.crop) : null;
  const pullQuote = extractPullQuote(section);

  // Decide where the pull quote sits - interleave with the longer of intro
  // or narrative for an editorial cadence rather than always-at-the-top.
  const hasLongNarrative = (section.narrative || '').length > 350;

  return (
    <section
      id={`section-${section.id}`}
      ref={registerRef}
      data-section-id={section.id}
      data-testid={`city-guide-section-${section.id}`}
      className="mb-20 scroll-mt-32"
    >
      {/* Ornamental divider */}
      <div className="flex items-center gap-4 mb-8" aria-hidden="true">
        <span className="block h-px flex-1 bg-[#0A1F2E]/15" />
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#0A1F2E] text-[#E8A830]">
          <Icon className="w-4 h-4" />
        </span>
        <span className="block h-px flex-1 bg-[#0A1F2E]/15" />
      </div>

      {/* Editorial section header */}
      <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#E8A830] text-center mb-2">
        Chapter {String(index).padStart(2, '0')}
      </p>
      <h2 className="font-display italic text-4xl md:text-5xl lg:text-6xl text-[#0A1F2E] text-center leading-[1.05] mb-10">
        {section.title}
      </h2>

      {/* Lede paragraph */}
      {section.intro && (
        <p className="font-display text-xl md:text-2xl text-[#0A1F2E]/85 leading-relaxed mb-8 first-letter:font-display first-letter:italic first-letter:text-5xl first-letter:font-semibold first-letter:text-[#1A5C6E] first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-none">
          {section.intro}
        </p>
      )}

      {/* Section image */}
      {sectionImage && (
        <figure className="my-10">
          <div
            className="w-full aspect-[16/9] rounded-lg bg-cover bg-center"
            style={{ backgroundImage: `url(${sectionImage})` }}
            aria-hidden="true"
          />
          <figcaption className="mt-3 font-mono uppercase tracking-[0.18em] text-[10px] text-[#0A1F2E]/50 text-center">
            {section.title}
          </figcaption>
        </figure>
      )}

      {/* Table (e.g., budget breakdown) */}
      {section.table && section.table.length > 0 && (
        <div className="overflow-x-auto my-8 bg-white border border-[#EFE6CF] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#F4ECD8]">
              <tr>
                {Object.keys(section.table[0]).map((k) => (
                  <th key={k} className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">
                    {k.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.table.map((row, i) => (
                <tr key={i} className="border-t border-[#EFE6CF]">
                  {Object.values(row).map((v, j) => (
                    <td key={j} className="px-4 py-3 text-[#0A1F2E]">
                      {typeof v === 'number' ? `$${v.toLocaleString('en-US', { timeZone: 'America/Chicago' })}` : (v || '-')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Neighborhoods grid */}
      {section.neighborhoods && section.neighborhoods.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4 my-8">
          {section.neighborhoods.map((n, i) => (
            <div key={i} className="bg-white border border-[#EFE6CF] rounded-xl p-5">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h4 className="font-display italic text-xl text-[#0A1F2E]">{n.name}</h4>
                {typeof n.rating_1_10 === 'number' && (
                  <span className="text-xs font-mono text-[#E8A830] whitespace-nowrap">{n.rating_1_10}/10</span>
                )}
              </div>
              {(n.rent_low_usd || n.rent_high_usd) && (
                <p className="text-xs font-mono text-[#2CA8A0] mb-1">
                  ${n.rent_low_usd}–${n.rent_high_usd}/mo
                </p>
              )}
              {n.vibe && (
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#1A5C6E] mb-2 font-mono">{n.vibe}</p>
              )}
              <p className="text-sm text-[#0A1F2E]/80 leading-relaxed">{n.notes}</p>
            </div>
          ))}
        </div>
      )}

      {/* Pull quote (placed before narrative for shorter sections, after for
          longer ones so the rhythm varies through the issue). */}
      {pullQuote && !hasLongNarrative && (
        <PullQuote text={pullQuote} />
      )}

      {/* Long-form narrative */}
      {section.narrative && (
        <div
          className="text-base md:text-lg text-[#0A1F2E]/85 leading-relaxed whitespace-pre-line"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: '1.125rem', lineHeight: 1.75 }}
        >
          {section.narrative}
        </div>
      )}

      {pullQuote && hasLongNarrative && (
        <PullQuote text={pullQuote} />
      )}
    </section>
  );
};

const PullQuote = ({ text }) => (
  <blockquote
    className="my-12 mx-auto max-w-2xl text-center px-6 py-2 border-y border-[#E8A830]/40"
    data-testid="city-guide-pull-quote"
  >
    <p
      className="font-display italic text-2xl md:text-3xl text-[#1A5C6E] leading-[1.25]"
      style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
    >
      <span className="text-[#E8A830] mr-1">“</span>
      {text}
      <span className="text-[#E8A830] ml-1">”</span>
    </p>
  </blockquote>
);

const RatingPanel = ({ scorecard }) => (
  <section
    className="bg-[#0A1F2E] text-white py-16"
    data-testid="city-guide-scorecard"
  >
    <div className="max-w-5xl mx-auto px-4 md:px-8">
      <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#E8A830] text-center mb-2">
        Editorial Ratings
      </p>
      <h2 className="font-display italic text-4xl md:text-5xl text-white text-center mb-12">
        City Scorecard
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
        {Object.entries(scorecard).map(([k, v]) => (
          <div key={k} className="border-t border-white/15 pt-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-white/55 mb-2">
              {k.replace(/_/g, ' ')}
            </p>
            <p className="font-display text-5xl text-white leading-none">
              {v}
              <span className="text-base text-white/35 font-display ml-1">/10</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ProfileSpread = ({ profiles }) => (
  <section className="max-w-5xl mx-auto px-4 md:px-8 py-16" data-testid="city-guide-profiles">
    <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#E8A830] text-center mb-2">
      Reader Match
    </p>
    <h2 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] text-center mb-12">
      Who should move here
    </h2>
    <div className="grid md:grid-cols-2 gap-5">
      {profiles.map((p, i) => (
        <article key={i} className="bg-white border border-[#EFE6CF] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-3">
            <Star className="w-4 h-4 text-[#E8A830]" />
            <span className="text-xs font-mono uppercase tracking-[0.18em] text-[#1A5C6E]">
              {p.fit_score_1_10}/10 fit
            </span>
          </div>
          <h3 className="font-display italic text-2xl text-[#0A1F2E] mb-2 leading-tight">
            {p.profile}
          </h3>
          <p className="text-sm text-[#0A1F2E]/80 leading-relaxed">{p.why}</p>
        </article>
      ))}
    </div>
  </section>
);


// ============== THIRD-VIEW UPSELL MODAL ==============
// Fires once, immediately, when a Guided user opens their 3rd auto-granted
// city guide. Backend returns show_third_view_upsell:true exactly once; the
// dismiss endpoint persists so it never re-appears on this user.
const ThirdViewUpsellModal = ({ cityName, onDismiss, onCheckout }) => (
  <div
    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
    data-testid="third-view-upsell-modal"
    onClick={onDismiss}
  >
    <div
      className="bg-white rounded-2xl w-full max-w-md p-8 relative shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 text-[#5A6B73] hover:text-[#0A1F2E] text-2xl leading-none"
        aria-label="Dismiss"
        data-testid="third-view-upsell-dismiss"
      >×</button>
      <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#E8A830] mb-2">
        Your 3rd free city guide
      </p>
      <h3 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] leading-[1.1] mb-3">
        Loved <span className="not-italic">{cityName}</span>?
      </h3>
      <p className="font-body text-base text-[#0A1F2E]/80 mb-6">
        Your next guide is <strong>$4.99</strong>. Compare another city with the same
        deep budget, safety, healthcare, and housing breakdowns - yours to keep.
      </p>
      <button
        onClick={onCheckout}
        data-testid="third-view-upsell-checkout"
        className="w-full bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-medium py-3 rounded-full transition"
      >
        Pick my next city - $4.99
      </button>
      <button
        onClick={onDismiss}
        data-testid="third-view-upsell-later"
        className="w-full mt-2 text-sm text-[#5A6B73] hover:text-[#0A1F2E] py-2"
      >
        Maybe later
      </button>
    </div>
  </div>
);

export default PortalCityGuideDetail;
